import * as React from 'react';
import './dashboard.scss';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'core/redux/store';
import { Overview } from 'core/models/overview/overview';
import axios from 'axios';
import { useRef, useState } from 'react';
import { ApiResult } from 'apis/apiresult';
import { Bar } from 'react-chartjs-2';
import BarChart from 'components/chart/chart';
import { BarElement, CategoryScale, Chart, LinearScale, BarController, Tooltip } from 'chart.js';
import { toShamsi, toShamsiDate } from 'utils/scripts/date';
import OrderOverviewBox from 'components/orders/order-overview-box';
import moment from 'moment';
import { ClipLoader } from 'react-spinners'; // Import the spinner from react-spinners
import { __API } from 'apis/api';
import PaymentOverviewBox from 'components/payments/payment-overview-box';
import CombineChartOrdersPayments from 'components/order-payment/combine-chart-orders-payments';
import OverviewBox from 'components/overview-box/overview-box';
import { DateTimeRangePicker } from 'components/datetime/rangepicker/rangepicker';
import CreditCartOutlined from '@ant-design/icons'
import { Subscriber } from 'core/domains/subscriber';
import { Order } from 'core/domains/order';
import { Account } from 'core/domains/account';
import { Payment } from 'core/domains/payment';
import OrderPieChartByType from 'components/chart/piechart/orders-type/order-pie-chart';
import OrderPieChartByChannels from 'components/chart/piechart/orders-channel/order-piechart-channel';
import OrderPieChartByTypes from 'components/chart/piechart/orders-type/order-pie-chart';
import SubscribersBarchart from 'components/chart/barcharts/subscribers/subscribers-barchart';
import AccountsBarchart from 'components/chart/barcharts/accounts/accounts-barchart';
import CheckAccountsBarchart from 'components/chart/barcharts/check-accounts/check-accounts-barchart';
import AccountPieChartByServices from 'components/chart/piechart/accounts-service/accounts-piechart-service';
import OrderPieChartByPayments from 'components/chart/piechart/orders-payment/orders-piechart-payment';

Chart.register(LinearScale, CategoryScale, BarController, BarElement, Tooltip);

const mapState = (state: RootState) => ({
    state: state,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;


const Dashboard: React.FC = (props) => {
    // State to hold the fetched data
    const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);


    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const from = moment().add(-1, 'day').format('YYYY-MM-DD');
    const to = moment().add(1, 'day').format('YYYY-MM-DD');

    const [startDate, setStartDate] = useState<string>(toShamsiDate(from));

    const [endDate, setEndDate] = useState<string>(toShamsiDate(to));

    // Use useEffect to fetch data when the component mounts
    React.useEffect(() => {
        const getAllSubscribers = async () => {
            try {
                const response = await axios.get<ApiResult<Subscriber[]>>(`${__API.overview.subscriberRange}/${startDate}/${endDate}`); // Replace with your API endpoint
                setSubscribers(response.data.value); // Set the fetched data to state
            } catch (err: any) {
                setError(err.message); // Set error message if the fetch fails
            } finally {
                setLoading(false); // Set loading to false after the fetch is complete
            }
        };

        const getAllOrders = async () => {
            try {
                const response = await axios.get<ApiResult<Order[]>>(`${__API.overview.orderRange}/${startDate}/${endDate}`); // Replace with your API endpoint
                setOrders(response.data.value); // Set the fetched data to state
            } catch (err: any) {
                setError(err.message); // Set error message if the fetch fails
            } finally {
                setLoading(false); // Set loading to false after the fetch is complete
            }
        };

        const getAllPayments = async () => {
            try {
                const response = await axios.get<ApiResult<Payment[]>>(`${__API.overview.paymentRange}/${startDate}/${endDate}`); // Replace with your API endpoint
                setPayments(response.data.value); // Set the fetched data to state
            } catch (err: any) {
                setError(err.message); // Set error message if the fetch fails
            } finally {
                setLoading(false); // Set loading to false after the fetch is complete
            }
        };

        const getAllAccounts = async () => {
            try {
                const response = await axios.get<ApiResult<Account[]>>(`${__API.overview.accountRange}/${startDate}/${endDate}`); // Replace with your API endpoint
                setAccounts(response.data.value); // Set the fetched data to state
            } catch (err: any) {
                setError(err.message); // Set error message if the fetch fails
            } finally {
                setLoading(false); // Set loading to false after the fetch is complete
            }
        };

        getAllSubscribers();
        getAllOrders();
        getAllPayments();
        getAllAccounts();

    }, [startDate, endDate]); // Empty dependency array means this effect runs once when the component mounts

    const getTotalValueOfOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0; // Initial value is 0
    const getTotalValueOfPayments = payments.length > 0 ? payments.filter(s => s.state.id == 1).reduce((accumulator, payment) => {
        return accumulator + payment.amount;
    }, 0) : 0; // I


    return (
        <>
            {
                !loading ?
                    <>
                        <div className="overview-container">
                            <DateTimeRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />

                            <div className="overview-boxes-row">
                                <OverviewBox
                                    hideCount
                                    title={'کاربران'}
                                    count={subscribers.filter(s => s.role.id == 0 && !s.referral).length}
                                />
                                <OverviewBox
                                    hideCount
                                    title={'همکاران'}
                                    count={subscribers.filter(s => s.role.id == 1).length}
                                />
                                <OverviewBox
                                    hideCount
                                    title={'زیرمجموعه ها'}
                                    count={subscribers.filter(s => s.role.id == 0 && s.referral).length}
                                />
                                <OverviewBox
                                    hideCount
                                    title={'سفارش ها'}
                                    count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id != 3).length : 0}
                                />
                                <OverviewBox
                                    hideCount
                                    title={'پرداخت ها'}
                                    count={payments.length > 0 ? payments.filter(s => s.state.id == 1).length : 0}
                                />

                                <OverviewBox
                                    hideCount
                                    title={'اشتراک جدید'}
                                    count={accounts.filter(s => s.type.id != 1).length}
                                />
                                <OverviewBox
                                    hideCount
                                    title={'اکانت تست'}
                                    count={accounts.filter(s => s.type.id == 1).length}
                                />
                            </div>


                            <div className="overview-charts-row">
                                <OrderOverviewBox
                                    title='گزارش سفارشات'
                                    data={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id != 3) : []}
                                    startDate={startDate}
                                    endDate={endDate} />
                                <PaymentOverviewBox
                                    title='گزارش پرداخت ها'
                                    data={payments.length > 0 ? payments.filter(s => s.state.id == 1) : []}
                                    startDate={startDate}
                                    endDate={endDate} />
                            </div>
                            <div className="overview-charts-row">
                                <CombineChartOrdersPayments
                                    title='گزارش کل درآمد'
                                    payments={payments.length > 0 ? payments.filter(s => s.state.id == 1) : []}
                                    orders={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id != 3) : []}
                                    startDate={startDate}
                                    endDate={endDate} />
                                <SubscribersBarchart
                                    title='گزارش کاربران'
                                    data={subscribers.length > 0 ? subscribers : []}
                                    startDate={startDate}
                                    endDate={endDate}
                                />
                            </div>
                            <div className="overview-charts-row">
                                <AccountsBarchart
                                    title='گزارش اشتراک ها'
                                    data={accounts.length > 0 ? accounts.filter(s => s.type.id == 0) : []}
                                    startDate={startDate}
                                    endDate={endDate} />
                                <CheckAccountsBarchart
                                    title='گزارش تست ارسالی'
                                    data={accounts.length > 0 ? accounts.filter(s => s.type.id == 1) : []}
                                    startDate={startDate}
                                    endDate={endDate}
                                />
                            </div>

                            <div className="overview-piecharts-row">
                                <OrderPieChartByPayments orders={orders.length > 0 ? orders.filter(s => s.state.id == 2) : []} />

                                <OrderPieChartByTypes orders={orders.length > 0 ? orders.filter(s => s.state.id == 2) : []} />

                            </div>

                            <div className="overview-piecharts-row">
                                <OrderPieChartByChannels orders={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.referral) : []} />
                                <AccountPieChartByServices accounts={accounts.length > 0 ? accounts.filter(s => s.type.id == 0) : []} />

                            </div>
                        </div>

                    </>

                    :
                    <div className="loader-container">
                        <ClipLoader size={50} color={"#123abc"} loading={loading} /> {/* Spinner */}
                    </div>

            }
        </>
    );
};

export default connector(Dashboard);
